import { api } from 'api/client';
import { API_ENDPOINTS } from 'config/api.config';
import {
  DashboardStats,
  AppStats,
  ConversionStats,
  UserAnalytics,
  PushPerformanceResponse,
  PushStatsResponse,
  ExportStatsParams,
} from 'types/push.types';
import { PushReport } from 'types/push.types';

export const statsApi = {
  getDashboard: async (appId: number): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>(
      API_ENDPOINTS.stats.dashboard,
      { app_id: appId }
    );
    return response.data;
  },

  getAppStats: async (appId: number, period?: string): Promise<AppStats> => {
    const response = await api.get<AppStats>(
      API_ENDPOINTS.stats.appStats(appId),
      { period }
    );
    return response.data;
  },

  getPushStats: async (messageId: string): Promise<PushStatsResponse> => {
    const response = await api.get<PushStatsResponse>(
      API_ENDPOINTS.stats.pushStats(messageId)
    );
    return response.data;
  },

  getPushReport: async (messageId: string): Promise<PushReport> => {
    const response = await api.get<PushReport>(
      API_ENDPOINTS.stats.pushReport(messageId)
    );
    return response.data;
  },

  getCampaignStats: async (campaignId: number): Promise<any> => {
    const response = await api.get(
      API_ENDPOINTS.stats.campaignStats(campaignId)
    );
    return response.data;
  },

  getConversions: async (
    appId: number,
    period?: string
  ): Promise<ConversionStats> => {
    const response = await api.get<ConversionStats>(
      API_ENDPOINTS.stats.conversions,
      { app_id: appId, period }
    );
    return response.data;
  },

  getUserAnalytics: async (
    appId: number,
    period: string = '30d',
    groupBy: string = 'day'
  ): Promise<{ app_id: number; period: string; group_by: string; analytics: UserAnalytics[] }> => {
    const response = await api.get(
      API_ENDPOINTS.stats.userAnalytics,
      { app_id: appId, period, group_by: groupBy }
    );
    return response.data;
  },

  getPushPerformance: async (
    appId: number,
    period?: string
  ): Promise<PushPerformanceResponse> => {
    const response = await api.get<PushPerformanceResponse>(
      API_ENDPOINTS.stats.pushPerformance,
      { app_id: appId, period }
    );
    return response.data;
  },

  compareCampaigns: async (campaignIds: number[]): Promise<{
    campaigns: Array<{
      id: number;
      name: string;
      stats: any;
    }>;
    warnings?: {
      inaccessible_campaigns: number[];
      message: string;
    };
  }> => {
    const response = await api.post(
      API_ENDPOINTS.stats.compareCampaigns,
      { campaign_ids: campaignIds }
    );
    return response.data;
  },

  compareSegments: async (data: {
    app_id: number;
    segment_ids: number[];
    period?: string;
  }): Promise<{
    app_id: number;
    period: string;
    segments: Array<{
      id: number;
      name: string;
      user_count: number;
      push_count: number;
      performance: {
        avg_ctr: number;
        conversion_rate: number;
      };
    }>;
    warnings?: {
      inaccessible_segments: number[];
      message: string;
    };
  }> => {
    const response = await api.post(
      API_ENDPOINTS.stats.compareSegments,
      data
    );
    return response.data;
  },

  exportStats: async (params: ExportStatsParams): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.stats.export,
      params
    );
    return response.data;
  },

  downloadStatsCSV: async (params: ExportStatsParams, filename?: string): Promise<void> => {
    const blob = await statsApi.exportStats(params);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `stats_${params.type}_${params.period || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};